<template>
  <div class="app-body">
    <Header>
      <!-- Debug button to send notifications -->
<!--      <button @click="triggerProjectChange">foo</button>-->
      <UserWidget ref="userWidget"></UserWidget>
    </Header>
    <main class="d-none container-fluid">
      <div class="row h-md-100 align-content-stretch">
        <div class="col-md-2" id="dashboard-menu">
          <DashboardMenu></DashboardMenu>
        </div>
        <div class="tab-content h-100 col-md-10">
          <div id="teams" :class="sectionPaneClass(paneActive.teams, true)" aria-labelledby="teams-pill" role="tabpanel"
               :aria-hidden="!paneActive.teams">
            <div id="team-pane" class="h-100">
              <TeamPane ref="teamPane"></TeamPane>
            </div>
          </div>
          <div id="billing" :class="sectionPaneClass(paneActive.billing, true)" aria-labelledby="billing-pill" role="tabpanel"
               :aria-hidden="!paneActive.billing">
            <div id="billing-pane" class="row">
              <BillingPane ref="billingPane"></BillingPane>
              <BillingAccountHistory ref="billingAccountHistoryPane"></BillingAccountHistory>
            </div>
          </div>
          <div id="settings" :class="sectionPaneClass(paneActive.settings)" aria-labelledby="settings-pill" role="tabpanel"
               :aria-hidden="!paneActive.settings">
            <div id="profile-pane">
              <ProfilePane ref="profilePane"></ProfilePane>
            </div>
          </div>
        </div>
      </div>
    </main>
    <div class="global-glasspane">
      <div class="d-flex justify-content-center align-items-center fa-4x h-100">
        <div><i class="fa fa-spin fa-spinner"></i>&nbsp;Loading&hellip;</div>
      </div>
    </div>
    <ErrorWidget ref="errorWidget"></ErrorWidget>
  </div>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-facing-decorator';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Header from './components/Header.vue';
import UserWidget from './components/UserWidget.vue';
import TeamPane from './components/TeamPane.vue';
import ProfilePane from './components/ProfilePane.vue';
import FadingElement from './utils/FadingElement';
import * as Auth from './auth/Auth';
import {
  NotificationDto, NotificationType,
  registerDefaultHandler as registerDefaultHandlerAtNotificationReceiver,
  registerHandler as registerAtNotificationReceiver, start as startNotificationReceiver
} from './NotificationReceiver';
import {loadTeam, TeamDto, UserDto} from './Teams';
import {updateHistory as projectItemUpdateHistory} from '@/components/ProjectItem.vue';
import {ClearRedirect, DownloadStuff, Signup, UserGet} from './Routes';
import {ERR_ACCOUNT_REMOVED} from './utils/ErrorMessages';
import {signOut} from './auth/Auth';
import DashboardMenu from '@/components/DashboardMenu.vue';
import VersionItem from './components/VersionItem.vue';
import InviteItem from './components/InviteItem.vue';
import MemberItem from './components/MemberItem.vue';
import ProjectItem from './components/ProjectItem.vue';
import ResourceItem from './components/ResourceItem.vue';
import CandidateItem from './components/CandidateItem.vue';
import ProjectLockModal from './components/ProjectLockModal.vue';
import ProjectRemoveModal from './components/ProjectRemoveModal.vue';
import ProjectNewModal from './components/ProjectNewModal.vue';
import {Dashboard, getDashboardSelection, setDashboard, updateDependencies} from './Dashboard';
import {installGoogleAnalytics} from './utils/Analytics';
import BillingPane from './components/BillingPane.vue';
import BillingAccountHistory from "@/components/BillingAccountHistory.vue";
import ErrorWidget from "@/components/ErrorWidget.vue";
import {setErrorWidget, takeDashboardFlashMessage} from "@/utils/Ui";

import $ from 'jquery';
import 'bootstrap';

type DashboardSection = 'teams' | 'billing' | 'settings';

@Component({
  components: {BillingAccountHistory, BillingPane, ErrorWidget, UserWidget, Header, TeamPane, ProfilePane, DashboardMenu}
})
export default class DashboardApp extends Vue {
  private dashboard?: Dashboard;

  public $refs!: {
    billingPane: BillingPane,
    billingAccountHistoryPane: BillingAccountHistory,
    errorWidget: ErrorWidget,
    profilePane: ProfilePane,
    teamPane: TeamPane,
    userWidget: UserWidget,
  };

  private get paneActive(): Record<DashboardSection, boolean> {
    const name = this.$route.name;
    return {
      teams: name !== 'billing' && name !== 'settings',
      billing: name === 'billing',
      settings: name === 'settings',
    };
  }

  private sectionPaneClass(active: boolean, fullHeight = false): Record<string, boolean> {
    return {
      'tab-pane': true,
      fade: true,
      'h-100': fullHeight,
      show: active,
      active,
    };
  }

  public mounted() {
    setErrorWidget(this.$refs.errorWidget);
    const globalGlasspane = new FadingElement('.global-glasspane');
    globalGlasspane.toggle(false);
    this.$refs.teamPane.teamList.globalGlasspane = globalGlasspane;
    this.dashboard = {
      teamPane: this.$refs.teamPane,
      vueTeams: this.$refs.teamPane.teamList,
      vueBell: this.$refs.userWidget.$refs.bell
    };
    setDashboard(this.dashboard);

    Auth.init().then((user) => {
      user.idToken().then((idToken) => {
        return UserGet.invoke({
          id: user.id(),
          idToken: idToken,
        });
      }).then((userDto: UserDto) => {
        window.currentUserId = user.id();
        this.$refs.profilePane.setUser(userDto);
        this.$refs.billingPane.setUser(user);
        this.$refs.billingPane.setHistoryPane(this.$refs.billingAccountHistoryPane);
        const flashMessage = takeDashboardFlashMessage();
        if (flashMessage) {
          this.$refs.errorWidget.showInfo(flashMessage);
        }
      }).catch(reason => {
        console.log(reason);
        if (reason.status === 403) {
          // Account removed - show error and sign out
          this.$refs.errorWidget.handleXhrError(reason, ERR_ACCOUNT_REMOVED);
          Auth.signOut();
        } else {
          Signup.redirect();
        }
      });
      ClearRedirect.invoke();

      $('main').removeClass('d-none');

      registerDefaultHandlerAtNotificationReceiver((dto: NotificationDto) =>
          loadTeam(dto.teamRefid).then((team: TeamDto) => {
            this.updateData(team);
            if (dto.author.id !== Auth.getUser().id()) {
              this.dashboard.vueBell.push(dto);
            }
          }));

      registerAtNotificationReceiver(NotificationType.ProjectLockStatusChange, (dto: NotificationDto) => {
        loadTeam(dto.teamRefid).then((team: TeamDto) => {
          this.updateData(team);
        });
      });

      registerAtNotificationReceiver(NotificationType.ProjectChange, (dto) => this.onProjectChange(dto));

      registerAtNotificationReceiver(NotificationType.ArchiveReady, (dto: NotificationDto) => {
        this.$refs.profilePane.archiveLink = `${DownloadStuff.absoluteUrl()}?archiveId=${dto.archiveId}`;
        this.$refs.profilePane.downloadStatus = 'ready';
      });

      registerAtNotificationReceiver(
          NotificationType.NewInvite,
          (dto: NotificationDto) => this.dashboard.vueBell.push(dto));

      startNotificationReceiver();

      $('.btn-logout').on('click', function() {
        signOut().then(function() {
          window.location.replace('/');
        });
      });
    }).catch(() => {
      Signup.redirect();
    });
  }

  private onProjectChange(dto: NotificationDto) {
    projectItemUpdateHistory(dto.projectRefid);
    const selection = getDashboardSelection();
    if (selection.project?.refid === dto.projectRefid) {
      setTimeout(() => selection.touch(), 2000);
    }
  }

  private updateData(team: TeamDto) {
    if (this.dashboard.vueTeams.team!.refid === team.refid) {
      updateDependencies(team);
    } else {
      this.dashboard.vueTeams.updateTeamData(team);
    }
  }

  /*
  // Debug function to send project change notifications (see "foo" button in the template)
  private triggerProjectChange() {
    this.onProjectChange({
      timestamp: 1,
      teamRefid: this.dashboard?.vueTeams?.team?.refid,
      projectRefid: this.dashboard?.teamPane?.getProject()?.refid,
      type: ""
    })
  }
  */
}

import type { App } from 'vue';

export function setupVueComponents(app: App) {
  app.component('version-item', VersionItem);
  app.component('invite-item', InviteItem);
  app.component('member-item', MemberItem);
  app.component('project-item', ProjectItem);
  app.component('resource-item', ResourceItem);
  app.component('candidate-item', CandidateItem);
  app.component('project-lock-modal', ProjectLockModal);
  app.component('project-remove-modal', ProjectRemoveModal);
  app.component('project-new-modal', ProjectNewModal);
  app.component('v-select', vSelect);
}

installGoogleAnalytics();
</script>

<style lang="scss">
@import "sass/dashboard";
</style>
